/**
 * Created by Jerker Nord on 2022-11-08.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {Loner} from "../../Loner";
import {IWinRatioConfig, LonerWinConfigs} from "../../configs/LonerWinConfigs";
import {LonerGameMode} from "../../server/LonerGameMode";
import {LonerBitmapCountUp} from "../../components/LonerBitmapCountUp";

export class LonerTotalWinView extends PIXI.Container implements StageLayer {

    private static FADE_IN_TIME:number = 0.3;

    private _totalWinContainer:PIXI.Container;
    private _totalWinCountUp:LonerBitmapCountUp;
    private _fadeInTimeline:gsap.core.Timeline;
    private _fadeOutTimeline:gsap.core.Timeline;
    private _countUpTimeline:gsap.core.Timeline;
    private _totalWinAmount:number;
    private _winRatioConfig:IWinRatioConfig;
    private _playSound:boolean = true;
    private _showCountUp:boolean;
    private _endSoundPlayed:boolean;
    private _targetScale:number;
    private _yPosWays:number;

    constructor() {
        super();
        this.name = "LonerTotalWinView";
        this.init();
    }

    private init() {
        this._totalWinContainer = new PIXI.Container();
        this._totalWinContainer.position.set(360, 310);
        this._totalWinContainer.visible = false;
        this.addChild(this._totalWinContainer);

        this._totalWinCountUp = new LonerBitmapCountUp(104);
        this._totalWinCountUp.visible = false;
        this._totalWinContainer.addChild(this._totalWinCountUp);
    }

    private setKeypadWin():void {
        Loner.keypadPlugin.setWin(Loner.model.data.accumulatedRoundWin, false, Loner.isWinBelowStake());
    }

    public getTotalWinTimeline(totalWin:number):gsap.core.Timeline {
        this._yPosWays = 40;
        let targetScale:number = 0.4;

        if(this._fadeOutTimeline && this._fadeOutTimeline.isActive()) {
            this._fadeOutTimeline.pause();
            this._fadeOutTimeline.kill();
        }

        this._totalWinAmount = totalWin;
        this._winRatioConfig = LonerWinConfigs.getWinRatioConfig();
        this._playSound = true;
        this._showCountUp = true;

        this._totalWinContainer.scale.set(0.5, 0.5);

        if(this._winRatioConfig.type == LonerWinConfigs.SMALL) {
            this._yPosWays = 50;
            targetScale = 0.4;
        } else if(this._winRatioConfig.type == LonerWinConfigs.MEDIUM) {
            this._yPosWays = 50;
            targetScale = 0.5;
        } else if(this._winRatioConfig.type == LonerWinConfigs.LARGE) {
            this._yPosWays = 64;
            targetScale = 0.5;
        }
        this._targetScale = targetScale;

        if(Loner.model.data.mode == LonerGameMode.NORMAL && (Loner.isWinBelowStake() || Loner.isWinBelowPlayedBetValue())) {
            this._playSound = false;
            this._showCountUp = false;
        }

        if(Loner.apiPlugin.gameClientConfiguration.belowStakeWinRestriction
            && Loner.model.data.mode !== LonerGameMode.NORMAL
            && this._totalWinAmount <= Loner.model.gameData.playedBetValue) {
            //We are in FS/SFS. Always play unless total win is less than base bet
            this._playSound = false;
            this._showCountUp = false;
        }
        this._endSoundPlayed = false;

        this._countUpTimeline = gsap.timeline();

        if(this._winRatioConfig.duration == 0) {
            if(this._showCountUp) {
                this._totalWinContainer.scale.set(targetScale, targetScale);
                this._totalWinCountUp.visible = true;
                this._totalWinCountUp.startFlicker();
                this._totalWinCountUp.getCountAnimation(this._totalWinAmount, this._totalWinAmount, this._winRatioConfig.duration);
                Loner.winWaysView.setYPosWays(this._yPosWays);
                Loner.winWaysView.showInitialWinWays();
            }
            this.setKeypadWin();
            if(this._playSound) {
                this._endSoundPlayed = true;
                Loner.soundPlayer.stopCountUp(this._winRatioConfig);
            }
        } else {
            if(this._playSound) {
                this._countUpTimeline.add(() => Loner.soundPlayer.startCountUp());
            }
            if(this._showCountUp) {
                this._countUpTimeline.add([
                    () => {
                        this._totalWinCountUp.visible = true;
                        this._totalWinCountUp.startFlicker();
                    },
                    this._totalWinCountUp.getCountAnimation(0, this._totalWinAmount, this._winRatioConfig.duration, true),
                    gsap.to(this._totalWinContainer.scale, {
                        duration : this._winRatioConfig.duration,
                        x : targetScale,
                        y : targetScale,
                        ease : "power3.out"
                    })
                ], 0);
                this._countUpTimeline.add(() => {
                    this._totalWinCountUp.stopAt(this._totalWinAmount);
                    if(this._playSound) {
                        this._endSoundPlayed = true;
                        Loner.soundPlayer.stopCountUp(this._winRatioConfig);
                    }

                    Loner.winWaysView.setYPosWays(this._yPosWays);
                    Loner.winWaysView.showInitialWinWays();
                });
            }

            this._countUpTimeline.add(() => {
                this.setKeypadWin();
            });
        }

        if(this._showCountUp) {
            this._totalWinContainer.alpha = 0;
            this._totalWinContainer.visible = true;

            this._fadeInTimeline = gsap.timeline();
            this._fadeInTimeline.add([
                gsap.to(this._totalWinContainer, {
                    duration : LonerTotalWinView.FADE_IN_TIME,
                    alpha : 1,
                    ease : "power3.out"
                })
            ]);

            if(this._countUpTimeline.totalDuration() == 0) {
                this._fadeInTimeline.add(() => this.fadeOutWinPres(0.2, 0.5));
                return this._fadeInTimeline;
            } else {
                this._countUpTimeline.add(() => this.fadeOutWinPres(0.2, 0.5));
                return this._countUpTimeline;
            }
        }

        return gsap.timeline();
    }

    private fadeOutWinPres(duration:number, delay:number):void {
        this._fadeOutTimeline = gsap.timeline({
            delay : delay
        });
        this._fadeOutTimeline.add([
            gsap.to(this._totalWinContainer, {
                duration : duration,
                alpha : 0,
                ease : "power3.out"
            }),
            () => {
                Loner.winWaysView.getHideToggleWin();
            }
        ]);

        this._fadeOutTimeline.add(() => {
            this._totalWinContainer.visible = false;
            this._totalWinCountUp.visible = false;
            this._totalWinCountUp.stopFlicker();
        });
    }

    public hardAbort():void {
        if(this._fadeInTimeline && this._fadeInTimeline.isActive()) {
            this._fadeInTimeline.pause();
            this._fadeInTimeline.kill();

            this.setTotalWinForAbort();
            this.fadeOutWinPres(0.3, 0);
        }

        if(this._countUpTimeline && this._countUpTimeline.isActive()) {
            this._countUpTimeline.pause();
            this._countUpTimeline.kill();

            this.setKeypadWin();

            this.setTotalWinForAbort();
            this.fadeOutWinPres(0.5, 0.6);

            if(this._playSound && !this._endSoundPlayed) {
                this._endSoundPlayed = true;
                Loner.soundPlayer.stopCountUp(this._winRatioConfig);
            }
        }
    }

    private setTotalWinForAbort():void {
        this._totalWinContainer.alpha = 1;
        this._totalWinContainer.scale.set(this._targetScale);
        this._totalWinContainer.visible = true;
        this._totalWinCountUp.visible = true;
        this._totalWinCountUp.startFlicker();
        this._totalWinCountUp.stopAt(this._totalWinAmount);
    }

    get yPosWays():number {
        return this._yPosWays;
    }
}